import { create } from "zustand";
import { syncStatusBar } from "@/lib/native/statusBar";

export type Theme = "light" | "dark" | "system";
const THEME_KEY = "pbranks_theme";
const SIDEBAR_KEY = "pbranks_sidebar";

function read<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T) || fallback;
  } catch {
    return fallback;
  }
}

export function applyTheme(theme: Theme) {
  const dark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
  // Native shells: keep the status-bar text legible over the page colour
  // (iOS draws it over the WebView, #379). No-op on the web.
  void syncStatusBar(dark);
}

type UiState = {
  theme: Theme;
  sidebarCollapsed: boolean;
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
};

export const useUi = create<UiState>((set, get) => ({
  theme: read<Theme>(THEME_KEY, "system"),
  sidebarCollapsed: read<string>(SIDEBAR_KEY, "open") === "collapsed",
  setTheme: (theme) => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
    applyTheme(theme);
    set({ theme });
  },
  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? "collapsed" : "open");
    } catch {
      /* ignore */
    }
    set({ sidebarCollapsed: next });
  },
}));
